import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/atoms/studio/ui/button";
import { EFFECTS_PAGE_SIZE, useEngineEffects } from "@/hooks/migrations/engine";
import { engineErrorMessage } from "@/services/migrations/engine";
import type { EngineCoverageBucket, EngineEffect } from "@/services/migrations/engine";

/**
 * One coverage bucket, one page at a time.
 *
 * `/report` does not ship the `carried` list — on a real Profile plan it is 838 KB of an
 * 876 KB payload, which is the part a human never reads and a browser always pays for.
 * The other four buckets go through the same endpoint not because they are large but
 * because "this bucket happens to be small today" is a property of the org someone last
 * looked at, and a reader should not have to learn which lists are safe to inline.
 *
 * THE TOTAL COMES FROM THE SERVER AND IS ALWAYS SHOWN NEXT TO THE PAGE. "There are more"
 * must never be inferred from a full page — that inference is wrong exactly once per
 * bucket, on the bucket whose size is an exact multiple of the page.
 */
export function EngineEffectsBrowser({
  planId,
  taskPath,
  bucket,
  /** The count the report already gave us, so the header is honest before the first page lands. */
  expectedTotal,
}: {
  planId: string;
  taskPath: string;
  bucket: EngineCoverageBucket;
  expectedTotal?: number;
}) {
  const [offset, setOffset] = useState(0);
  const page = useEngineEffects(planId, taskPath, { bucket, offset, limit: EFFECTS_PAGE_SIZE });

  if (page.isLoading && !page.data) {
    return <p className="font-mono text-xs text-muted-foreground">Loading {bucket}…</p>;
  }
  if (page.isError) {
    return (
      <p className="font-mono text-xs text-destructive">
        This bucket could not be read: {engineErrorMessage(page.error)}. The count beside it came
        from the report and still stands; the list is missing from this view only.
      </p>
    );
  }
  const data = page.data;
  if (!data) return null;

  const total = data.total;
  const start = data.items.length === 0 ? 0 : data.offset + 1;
  const end = data.offset + data.items.length;
  const hasPrev = data.offset > 0;
  const hasNext = end < total;

  if (total === 0) {
    return (
      <p className="font-mono text-xs text-muted-foreground">
        Nothing in this bucket.
        {expectedTotal != null && expectedTotal > 0 ? (
          <span className="text-destructive">
            {" "}
            The report counted {expectedTotal} — these two disagree, which is worth looking at.
          </span>
        ) : null}
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">
          showing {start}–{end} of {total}
        </span>
        {(hasPrev || hasNext) && (
          <div className="flex items-center gap-1.5">
            <Button
              variant="outline"
              size="sm"
              disabled={!hasPrev || page.isFetching}
              onClick={() => setOffset(Math.max(0, offset - EFFECTS_PAGE_SIZE))}
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={!hasNext || page.isFetching}
              onClick={() => setOffset(offset + EFFECTS_PAGE_SIZE)}
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </div>

      <ul className="divide-y divide-border">
        {data.items.map((effect, index) => (
          <EffectRow key={`${effect.kind}-${effect.target}-${index}`} effect={effect} />
        ))}
      </ul>
    </div>
  );
}

function EffectRow({ effect }: { effect: EngineEffect }) {
  return (
    <li className="space-y-0.5 py-1.5">
      <div className="flex flex-wrap items-baseline gap-x-2 font-mono text-[11px]">
        <span className="text-muted-foreground">{effect.kind}</span>
        <span className="break-all text-foreground">{effect.target}</span>
        {effect.strength.length > 0 ? (
          <span className="text-muted-foreground/70">{effect.strength.join(" + ")}</span>
        ) : null}
        {effect.code ? (
          <span className="text-[hsl(var(--accent-warm))]">{effect.code}</span>
        ) : null}
      </div>
      {effect.reason ? (
        <p className="max-w-prose text-[11px] text-muted-foreground">{effect.reason}</p>
      ) : null}
      {effect.manual_step ? (
        <p className="max-w-prose text-[11px] text-[hsl(var(--accent-warm))]">
          <span className="font-mono uppercase">manual step: </span>
          {effect.manual_step}
        </p>
      ) : null}
    </li>
  );
}
